import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Siren, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { SectionClouds } from "@/components/landing";
import { CtaBand } from "@/components/CtaBand";
import { ProcessFlowDiagram } from "./ProcessFlowDiagram";

interface MeasuredItem {
  tag: string;
  num: string;
  text: string;
  linkLabel: string;
  href: string;
}

/** Shared reveal-on-scroll pattern used across the landing sections. */
function useReveal<T extends HTMLElement>() {
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<T>(null);
  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, isVisible };
}

const reveal = (isVisible: boolean, delay = 0) => ({
  className: cn(
    "transition-all duration-700",
    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
  ),
  style: { transitionDelay: isVisible ? `${delay}ms` : "0ms" },
});

const TRUST_BLOCKS = [
  { key: "security", para: "securityP", Icon: ShieldCheck },
  { key: "support", para: "supportP", Icon: Siren },
  { key: "exit", para: "exitP", Icon: KeyRound },
] as const;

export function ProcessContent() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const items = t("processPage.measured.items", {
    returnObjects: true,
  }) as MeasuredItem[];

  const { ref: heroRef, isVisible: heroVisible } = useReveal<HTMLElement>();
  const { ref: howRef, isVisible: howVisible } = useReveal<HTMLElement>();
  const { ref: trustRef, isVisible: trustVisible } = useReveal<HTMLElement>();
  const { ref: measuredRef, isVisible: measuredVisible } = useReveal<HTMLElement>();

  return (
    <>
      {/* Hero + flow diagram */}
      <section ref={heroRef} className="frame bg-page-bg">
        <SectionClouds variant="case-study" />
        <div className="container-narrow section-pad relative z-10 !pt-10">
          <div
            {...reveal(heroVisible)}
            className={cn(reveal(heroVisible).className, "max-w-[760px]")}
          >
            <span className="eyebrow">{t("processPage.eyebrow")}</span>
            <h1 className="mt-4 text-[clamp(36px,5.6vw,68px)] leading-[1.04] font-medium tracking-[-0.035em] text-ink">
              <Trans
                i18nKey="processPage.title"
                components={{
                  italic: <span className="serif-italic text-accent" />,
                }}
              />
            </h1>
            <p className="mt-6 max-w-[58ch] text-[clamp(15px,1.4vw,17px)] leading-[1.55] text-ink-2">
              {t("processPage.lede")}
            </p>
          </div>

          <div
            {...reveal(heroVisible, 150)}
            className={cn(reveal(heroVisible, 150).className, "mt-8 lg:mt-10")}
          >
            <ProcessFlowDiagram />
          </div>
        </div>
      </section>

      {/* 01 — How it works: two pillars */}
      <section ref={howRef} className="frame bg-surface-2/40">
        <SectionClouds variant="benefits" />
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(howVisible)}>
            <span className="eyebrow">
              <span className="num">01 —</span> {t("processPage.how.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("processPage.how.titleLead")}{" "}
              <span className="serif-italic">{t("processPage.how.titleEm")}</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-7">
            {(["section1", "section2"] as const).map((sec, i) => (
              <div
                key={sec}
                {...reveal(howVisible, i * 100)}
                className={cn(
                  reveal(howVisible, i * 100).className,
                  "lift rounded-[22px] border border-line bg-surface p-8 sm:p-9",
                )}
              >
                <div className="mono text-[11px] tracking-[0.14em] text-accent">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-4 text-[clamp(24px,2.4vw,32px)] leading-[1.15] font-medium tracking-[-0.025em] text-balance text-ink">
                  {t(`processPage.${sec}.titleLead`)}{" "}
                  <span className="serif-italic text-accent">
                    {t(`processPage.${sec}.titleEm`)}
                  </span>
                </h3>
                <p className="mt-4 text-[14.5px] leading-[1.6] text-ink-2">
                  {t(`processPage.${sec}.p1`)}
                </p>
                <p className="mt-3 text-[14.5px] leading-[1.6] text-ink-2">
                  {t(`processPage.${sec}.p2`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 — Trust: security, support, exit */}
      <section ref={trustRef} className="frame bg-page-bg">
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(trustVisible)}>
            <span className="eyebrow">
              <span className="num">02 —</span> {t("processPage.section3.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("processPage.section3.titleLead")}{" "}
              <span className="serif-italic">{t("processPage.section3.titleEm")}</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3 lg:gap-7">
            {TRUST_BLOCKS.map(({ key, para, Icon }, i) => (
              <div
                key={key}
                {...reveal(trustVisible, i * 80)}
                className={cn(
                  reveal(trustVisible, i * 80).className,
                  "lift rounded-[22px] bg-ink p-7 text-page-bg shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)]",
                )}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <div className="mono mt-4 text-[11px] tracking-[0.16em] text-page-bg/55 uppercase">
                  {t(`processPage.section3.labels.${key}`)}
                </div>
                <p className="mt-2 text-[14px] leading-[1.6] text-page-bg/80">
                  {t(`processPage.section3.${para}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — How we measure (anchor target for StatsStrip links) */}
      <section ref={measuredRef} id="measured" className="frame scroll-mt-28 bg-surface-2/40">
        <SectionClouds variant="faq" />
        <div className="container-narrow section-pad relative z-10">
          <div {...reveal(measuredVisible)}>
            <span className="eyebrow">
              <span className="num">03 —</span> {t("processPage.measured.eyebrow")}
            </span>
            <h2 className="section-title">
              {t("processPage.measured.titleLead")}{" "}
              <span className="serif-italic">{t("processPage.measured.titleEm")}</span>
            </h2>
            <p className="section-lede">{t("processPage.measured.p1")}</p>
          </div>

          <div
            {...reveal(measuredVisible, 120)}
            className={cn(
              reveal(measuredVisible, 120).className,
              "mt-10 divide-y divide-line overflow-hidden rounded-[22px] border border-line bg-surface",
            )}
          >
            {items.map((item) => (
              <div
                key={item.num}
                className="grid grid-cols-1 items-start gap-2 px-7 py-6 sm:grid-cols-[150px_1fr] sm:gap-6"
              >
                <div className="text-[clamp(26px,3vw,34px)] leading-none font-semibold tracking-[-0.03em] whitespace-nowrap text-ink">
                  {item.num}
                </div>
                <div>
                  <div className="mono text-[10px] tracking-[0.16em] text-ink-3 uppercase">
                    {item.tag}
                  </div>
                  <div className="mt-1 text-[14.5px] leading-[1.6] text-ink-2">
                    {item.text}
                    {item.linkLabel ? (
                      item.href ? (
                        <>
                          {" "}
                          <Link
                            to={localizedHref(item.href)}
                            className="font-medium whitespace-nowrap text-accent transition-colors hover:text-ink"
                          >
                            {item.linkLabel}
                          </Link>
                        </>
                      ) : (
                        <>
                          {" "}
                          <span className="text-ink-3">{item.linkLabel}</span>
                        </>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-7 text-[14px] leading-[1.65] text-ink-2">
            <Link to={localizedHref("/work")} className="transition-colors hover:text-accent">
              {t("processPage.measured.outcomesNote")}
            </Link>
          </p>
          <p className="mt-4 max-w-[80ch] text-[13px] leading-[1.65] text-ink-3 italic">
            {t("processPage.measured.caveat")}
          </p>
        </div>
      </section>

      <CtaBand
        titleLead={t("processPage.cta.titleLead")}
        rotateWords={t("processPage.cta.words", { returnObjects: true }) as string[]}
        bookLabel={t("processPage.cta.bookLink")}
        secondaryLabel={t("processPage.cta.fitLink")}
        secondaryTo="/fit"
        ghostLabel={t("processPage.cta.aboutLink")}
        ghostTo="/about"
        trackSource="process"
      />
    </>
  );
}
